<template>
  <b-card class="department-settings">
    <b-alert v-if="err" show variant="danger">
      <p><pre>{{ err }}</pre></p>
    </b-alert>
    <template v-else-if="!company">
      <no-ssr><loading-spinner class="d-flex justify-content-center my-5"/></no-ssr>
    </template>
    <template v-else>
      <h3>{{departmentLabelPlural}}</h3>
      <p>Create &amp; customise {{departmentLabelPlural.toLowerCase()}} to best describe the structure of your company</p>
      <p>These don't necessarily need to be the physical {{departmentLabelPlural.toLowerCase()}} of your company, logical groups of employees will work just as well</p>
      <p>{{departmentLabelPlural}} are used both to group jobs &amp; leaderboards</p>

      <div class="d-flex flex-row justify-content-between align-content-center my-2">
        <p class="d-flex flex-column justify-content-center m-0">Enable {{departmentLabelPlural}}</p>
        <toggle class="m-0" style="width: 200px;"
          label-true="On" label-false="Off"
          v-model="form.useDepartments" @input="togglePreferences"/>
      </div>

      <div v-if="form.useDepartments">
        Edit your {{departmentLabelPlural.toLowerCase()}} or create new ones
        <hr class="mb-3"/>
        <p class="my-5 text-center" v-if="form.departments.length === 0 && form.newDepartments.length ===0">
          There are currently no {{departmentLabelPlural.toLowerCase()}}, please click below to add one.
        </p>
        <div v-for="department in form.departments" :key="department.id">
          <b-input-group class="mb-3">
            <b-form-input v-model="department.name" @input="v => updateFormInput(department.id, { name: v })"/>
            <b-input-group-append>
              <b-dropdown variant="primary">
                <template v-slot:button-content><font-awesome-icon :icon="department.icon"/></template>
                <b-dropdown-item v-if="showIcons === department.id" @click="toggleIconBlock(department.id)">Hide Icons</b-dropdown-item>
                <b-dropdown-item v-else @click="toggleIconBlock(department.id)">Edit Icon</b-dropdown-item>
                <b-dropdown-item @click="toggleDeleteDepartmentBlock(department.id)">Delete</b-dropdown-item>
              </b-dropdown>
            </b-input-group-append>
            <icon-picker v-if="showIcons === department.id" :value="department.icon" @input="icon => updateFormInput(department.id, { icon })"/>
          </b-input-group>
          <b-row class="my-3" v-if="deleteDepartmentBlock === department.id">
            <b-col md="8">
              <h4>Are you sure you want to delete this {{departmentLabel.toLowerCase()}}?</h4>
            </b-col>
            <b-col md="4">
              <b-button-group class="pull-right">
                <b-btn variant="outline-primary" @click="() => deleteDepartmentBlock = null">Cancel</b-btn>
                <b-btn variant="outline-danger" @click="deleteDepartment(department.id)">Delete</b-btn>
              </b-button-group>
            </b-col>
          </b-row>
        </div>
        <div v-for="(department, i) in form.newDepartments" :key="i">
          <b-input-group class="mb-3">
            <b-form-input v-model="department.name" />
            <b-input-group-append>
              <b-dropdown variant="primary">
                <template v-slot:button-content><font-awesome-icon :icon="department.icon"/></template>
                <b-dropdown-item v-if="showIcons === i" @click="toggleIconBlock(i)">Hide Icons</b-dropdown-item>
                <b-dropdown-item v-else @click="toggleIconBlock(i)">Edit Icon</b-dropdown-item>
                <b-dropdown-item @click="form.newDepartments.splice(i, 1)">Delete</b-dropdown-item>
              </b-dropdown>
            </b-input-group-append>
            <icon-picker v-if="showIcons === i" :value="department.icon" @input="value => setIcon(form.newDepartments, i, value)"/>
          </b-input-group>
        </div>

        <b-btn variant="outline-primary" @click="addDepartment">Add New</b-btn>
        <b-btn variant="primary" class="float-right" @click="save">Save</b-btn>

        <div class="d-flex flex-row justify-content-between align-content-center mt-2">
          <p class="d-flex flex-column justify-content-center m-0">Ask users to select their own {{departmentLabel.toLowerCase()}}</p>
          <toggle class="m-0" style="width: 200px;" label-true="Yes" label-false="No" v-model="form.askUserForDepartment" @input="togglePreferences"/>
        </div>
      </div>
    </template>
  </b-card>
</template>

<script>
import _debounce from 'lodash/debounce';
import gql from 'graphql-tag';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import Toggle from '@/components/misc/toggle';
import IconPicker from '@/components/misc/iconPicker';
import { mapRoot } from '@src/mixins/root';

const DepartmentSettingsFragment = gql`
  fragment DepartmentSettingsDetails on Department {
    id
    name
    icon
    status
  }
`;

const departmentToggleMutation = gql`
  mutation departmentToggle(
    $useDepartments: Boolean!,
    $askUserForDepartment: Boolean!,
  ) {
    updateCompany(update: {
      preferences: {
        useDepartments: $useDepartments,
        askUserForDepartment: $askUserForDepartment,
      },
    }) {
      id
      preferences {
        useDepartments
        askUserForDepartment
      }
    }
  }
`;

const updateDepartmentsMutation = gql`
  ${DepartmentSettingsFragment}

  mutation updateManyDepartments($update: UpdateDepartmentInput!, $departmentID: ID!) {
    updateManyDepartments(update: $update, where: {id: $departmentID}) {
      ...DepartmentSettingsDetails
    }
  }
`;

const departmentCreateMutation = gql`
  ${DepartmentSettingsFragment}

  mutation createManyDepartments($create: [CreateDepartmentInput!]!) {
    createManyDepartments(create: $create) {
      ...DepartmentSettingsDetails
    }
  }
`;
const deleteDepartmentMutation = gql`
  mutation deleteDepartment($departmentID: ID!) {
    deleteDepartment(where: {id: $departmentID})
  }
`;

const SettingsQuery = gql`
  ${DepartmentSettingsFragment}

  query DepartmentPreferences {
    company {
      id
      name

      branding {
        smallLogo {
          id
        }
      }

      departments {
        ...DepartmentSettingsDetails
      }

      preferences {
        useDepartments
        askUserForDepartment
      }
    }
  }
`;

export default {
  name: 'CompanyDepartmentSettings',
  components: {
    LoadingSpinner,
    Toggle,
    IconPicker,
  },
  data: () => ({
    company: null,
    err: null,
    form: {
      useDepartments: false,
      askUserForDepartment: false,
      departments: [
        {
          name: 'Marketing',
          id: 1,
          icon: 'users',
        },
        {
          name: 'Sales',
          id: 2,
          icon: 'users',
        },

      ],
      newDepartments: [],
    },
    showIcons: null,
    deleteDepartmentBlock: null,
  }),
  metaInfo: {
    title: 'Department Settings',
  },
  apollo: {
    company: {
      query: SettingsQuery,
      result({ loading, data }) {
        if (!loading) {
          this.onFormReset();
        }
      },
      error(err) {
        this.err = err;
      },
    },
  },
  computed: {
    ...mapRoot([ 'departmentLabel', 'departmentLabelPlural' ]),
  },
  methods: {
    onFormReset() {
      const { company } = this;
      this.form.departments = company.departments;
      this.form.useDepartments = company.preferences.useDepartments;
      this.form.askUserForDepartment = company.preferences.askUserForDepartment;
    },
    save() {
      if (this.form.newDepartments.length > 0) {
        this.createDepartments();
      }
    },
    async togglePreferences() {
      console.log(this.form);

      await this.$apollo.mutate({
        mutation: departmentToggleMutation,
        variables: {
          useDepartments: this.form.useDepartments,
          askUserForDepartment: this.form.askUserForDepartment,
        },
        update(store, { data: { preferences } }) {
          const data = store.readQuery({ query: SettingsQuery });
          Object.assign(data.company.preferences, preferences);
          store.writeQuery({ query: SettingsQuery, data });
        },
      });
    },
    async createDepartments() {
      await this.$apollo.mutate({
        mutation: departmentCreateMutation,
        variables: {
          create: this.form.newDepartments.map(department => {
            delete department.id;
            return department;
          }),
        },
        update(store, { data: { createManyDepartments: departments } }) {

          const data = store.readQuery({ query: SettingsQuery });
          data.company.departments.push(...departments);
          store.writeQuery({ query: SettingsQuery, data });
        }
      });
      this.form.newDepartments = [];
    },
    addDepartment() {
      if (this.form.newDepartments) {
        this.form.newDepartments.push({ id: this.form.newDepartments.length, name: null, icon: 'users', status: 'ACTIVE' });
      }
    },
    async deleteDepartment(id) {
      await this.$apollo.mutate({
        mutation: deleteDepartmentMutation,
        variables: { departmentID: id },
        update(store, { data: { createManyDepartments: departments } }) {
          const data = store.readQuery({ query: SettingsQuery });
          data.company.departments = data.company.departments.filter(department => department.id !== id);
          store.writeQuery({ query: SettingsQuery, data });
        }
      });
      this.deleteDepartmentBlock = null;
    },
    toggleIconBlock(id) {
      if (this.showIcons === null) {
        this.showIcons = id;
        this.deleteDepartmentBlock = null;
      } else {
        this.showIcons = null;
      }
    },
    toggleDeleteDepartmentBlock(id) {
      if (this.deleteDepartmentBlock === null) {
        this.deleteDepartmentBlock = id;
        this.showIcons = null;
      } else {
        this.deleteDepartmentBlock = null;
      }
    },
    setIcon(array, id, icon) {
      array.forEach(department => {
        if (department.id === id) {
          department.icon = icon;
        }
      });
      this.showIcons = null;
    },
    updateFormInput: _debounce(async function (departmentID, update) {
      await this.$apollo.mutate({
        mutation: updateDepartmentsMutation,
        variables: { departmentID, update },
      });
      this.showIcons = null;
    }, 500),
  },

};
</script>

<style lang="scss">
.department-settings {
  .btn-group {
    .btn {
      min-width: 100px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/settings/Departments.vue